/**
 * Editable site/workspace copy. Persisted via the tenant-scoped store
 * (`devStorage`), so when an admin is signed in it mirrors to MongoDB
 * (WorkspaceData KV) and rehydrates on login. Defaults are empty.
 */

import { useCallback, useEffect, useState } from 'react';
import { loadDevJson, saveDevJson } from './devStorage';

const STORAGE_KEY = 'edgefin_site_content_v1';

export type LandingReview = {
  name: string;
  role: string;
  image: string;
  review: string;
  rating: number;
};

export const DEFAULT_LANDING_REVIEWS: LandingReview[] = [
  {
    name: 'Ramesh Kumar',
    role: 'Owner, Sri Lakshmi Auto Finance — Hyderabad',
    image: '',
    review:
      'We onboarded three branches in one week. Each branch has its own ledger login, and HQ still sees every collection in real time. Day reports replaced our evening Excel chaos.',
    rating: 5,
  },
  {
    name: 'Priya Menon',
    role: 'Operations Head, Metro Vehicle Finance — Kochi',
    image: '',
    review:
      'HP origination, hand loans, and customer WhatsApp reminders in one app. Our field staff record payments on mobile without fighting the UI.',
    rating: 5,
  },
  {
    name: 'Suresh Reddy',
    role: 'Partner, Reddy Motors Finance — Vijayawada',
    image: '',
    review:
      'Branch ledger books were the game changer. Collections teams log in to their book only; I switch to HQ view when I need the full picture.',
    rating: 5,
  },
  {
    name: 'Anitha Sharma',
    role: 'Admin, North Star Finance — Pune',
    image: '',
    review:
      'Razorpay billing, role-based access for employees, and branded PDF exports — it feels like software built for Indian finance shops, not a generic CRM.',
    rating: 5,
  },
  {
    name: 'Vikram Patel',
    role: 'Director, Patel Hire Purchase — Ahmedabad',
    image: '',
    review:
      'Seized vehicle workflow, EMI ledger, and customer profiles finally live in one place. Launch was smooth and the mobile experience is excellent.',
    rating: 5,
  },
];

export type ContactBlock = {
  phone: string;
  email: string;
  address: string;
  hours: string;
};

export const DEFAULT_CONTACT: ContactBlock = {
  phone: '+91 7799001866',
  email: 'support@edgefinances.com',
  address: 'Hitech City, Hyderabad, Telangana',
  hours: 'Mon–Sat · 9 AM – 7 PM IST',
};

export type HeroPreviewStats = {
  collectionsLabel: string;
  collectionsValue: string;
  collectionsChange: string;
  activeFacilities: string;
  handLoans: string;
  /** Bar heights 0–100 for the mini chart */
  chartHeights: number[];
};

export type PricingPlanContent = {
  id: string;
  name: string;
  price: string;
  description: string;
  features: string[];
  popular: boolean;
};

export type DashboardReview = {
  id: number;
  name: string;
  avatar: string;
  rating: number;
  comment: string;
  date: string;
  likes: number;
  replies: number;
};

export type ProfileDisplay = {
  name: string;
  id: string;
  email: string;
  phone: string;
  address: string;
  avatar: string;
};

export type SiteContent = {
  version: 1;
  landingReviews: LandingReview[];
  contact: ContactBlock;
  heroStats: HeroPreviewStats;
  pricingPlans: PricingPlanContent[];
  dashboardReviews: DashboardReview[];
  profileDisplay: ProfileDisplay;
};

export function defaultSiteContent(): SiteContent {
  return {
    version: 1,
    landingReviews: DEFAULT_LANDING_REVIEWS,
    contact: { ...DEFAULT_CONTACT },
    heroStats: {
      collectionsLabel: 'Collections this month',
      collectionsValue: '₹42.8L',
      collectionsChange: '+18% vs last month',
      activeFacilities: '186',
      handLoans: '54',
      chartHeights: [42, 68, 55, 82, 71, 90, 76],
    },
    pricingPlans: [],
    dashboardReviews: [],
    profileDisplay: {
      name: '',
      id: '',
      email: '',
      phone: '',
      address: '',
      avatar: '',
    },
  };
}

function mergeWithDefaults(parsed: unknown): SiteContent {
  const d = defaultSiteContent();
  if (!parsed || typeof parsed !== 'object') return d;
  const p = parsed as Partial<SiteContent>;
  const heroBase = { ...d.heroStats, ...(p.heroStats && typeof p.heroStats === 'object' ? p.heroStats : {}) };
  return {
    version: 1,
    landingReviews:
      Array.isArray(p.landingReviews) && p.landingReviews.length > 0
        ? (p.landingReviews as LandingReview[])
        : d.landingReviews,
    contact: {
      phone: (p.contact?.phone || '').trim() || DEFAULT_CONTACT.phone,
      email: (p.contact?.email || '').trim() || DEFAULT_CONTACT.email,
      address: (p.contact?.address || '').trim() || DEFAULT_CONTACT.address,
      hours: (p.contact?.hours || '').trim() || DEFAULT_CONTACT.hours,
    },
    heroStats: {
      ...heroBase,
      collectionsValue: heroBase.collectionsValue === '—' ? d.heroStats.collectionsValue : heroBase.collectionsValue,
      collectionsChange: heroBase.collectionsChange === '—' ? d.heroStats.collectionsChange : heroBase.collectionsChange,
      activeFacilities: heroBase.activeFacilities === '—' ? d.heroStats.activeFacilities : heroBase.activeFacilities,
      handLoans: heroBase.handLoans === '—' ? d.heroStats.handLoans : heroBase.handLoans,
      chartHeights:
        Array.isArray(p.heroStats?.chartHeights) && (p.heroStats!.chartHeights as number[]).length > 0
          ? (p.heroStats!.chartHeights as number[])
          : heroBase.chartHeights.length > 0
            ? heroBase.chartHeights
            : d.heroStats.chartHeights,
    },
    pricingPlans: Array.isArray(p.pricingPlans) ? (p.pricingPlans as PricingPlanContent[]) : d.pricingPlans,
    dashboardReviews: Array.isArray(p.dashboardReviews) ? (p.dashboardReviews as DashboardReview[]) : d.dashboardReviews,
    profileDisplay: { ...d.profileDisplay, ...(p.profileDisplay && typeof p.profileDisplay === 'object' ? p.profileDisplay : {}) },
  };
}

export function readSiteContent(): SiteContent {
  return mergeWithDefaults(loadDevJson<SiteContent | null>(STORAGE_KEY, null));
}

export function writeSiteContent(next: SiteContent): void {
  saveDevJson(STORAGE_KEY, next);
  window.dispatchEvent(new Event('edgefin-site-content'));
}

export function patchSiteContent(partial: Partial<SiteContent>): void {
  const cur = readSiteContent();
  writeSiteContent({ ...cur, ...partial, version: 1 });
}

export function resetSiteContentToDefaults(): void {
  writeSiteContent(defaultSiteContent());
}

const SITE_CONTENT_EVENT = 'edgefin-site-content';

export function useSiteContent(): {
  content: SiteContent;
  setContent: (next: SiteContent) => void;
  patch: (partial: Partial<SiteContent>) => void;
  refresh: () => void;
} {
  const [content, setContent] = useState<SiteContent>(() => readSiteContent());

  const refresh = useCallback(() => {
    setContent(readSiteContent());
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) refresh();
    };
    const onLocal = () => refresh();
    window.addEventListener('storage', onStorage);
    window.addEventListener(SITE_CONTENT_EVENT, onLocal);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(SITE_CONTENT_EVENT, onLocal);
    };
  }, [refresh]);

  const setContentPersist = useCallback((next: SiteContent) => {
    writeSiteContent(next);
    setContent(next);
  }, []);

  const patch = useCallback((partial: Partial<SiteContent>) => {
    const next = { ...readSiteContent(), ...partial, version: 1 as const };
    writeSiteContent(next);
    setContent(next);
  }, []);

  return { content, setContent: setContentPersist, patch, refresh };
}
